import Plotly, { PlotlyHTMLElement } from '@newcrom/plotly.js';
import { Callback, Nullable } from '@/types/utility';
import {
  Config,
  DataExtended,
  Layout,
  Measurement,
  HorizontalZoomBounds,
} from '@/uikitProject/charts/new/private/types';
import {
  AXIS_HOVER_FORMAT,
  COLORS,
  CONFIG,
  DATA_HOVER_TEMPLATE_FOR_PRESSURE,
  SECONDS_IN_MINUTE,
  SIZES,
} from '@/uikitProject/charts/new/private/constants';
import {
  getHoverMessages,
  numberFormatterWith2FractionDigits,
} from '@/uikitProject/charts/new/private/helpers';

type InitialOptions = {
  container: HTMLElement;
  measurement: Measurement;
  expectedDurationSeconds: number;
  isInProgress: boolean;
  widthPx: number;
};

type RenderOptions = {
  zoomBounds: Nullable<Partial<HorizontalZoomBounds>>;
  expectedDurationSeconds: number;
  isInProgress: boolean;
  widthPx: number;
};

type Actions = {
  zoom: Callback<[zoomBounds: Partial<HorizontalZoomBounds>]>;
  setExpectedDurationSeconds: Callback<[expectedDurationSeconds: number]>;
  setIsInProgress: Callback<[isInProgress: boolean]>;
  setWidth: Callback<[widthPx: number]>;
};

export class PressureDrawer {
  private chart: Nullable<PlotlyHTMLElement> = null;
  private config: Config = CONFIG;
  private renderOptions: RenderOptions = {
    zoomBounds: null,
    expectedDurationSeconds: 0,
    isInProgress: false,
    widthPx: 0,
  };
  private currentMeasurement: Measurement;

  private mediaQueryIsMobileSize: MediaQueryList = window.matchMedia(
    `(max-width: ${SIZES.mobileScreenSizePx}px)`,
  );

  // TODO make private and add public methods?
  public actions: Actions = {
    zoom: (zoomBounds: Partial<HorizontalZoomBounds>) => {
      this.renderOptions.zoomBounds = zoomBounds;
      this.refresh();
    },
    setExpectedDurationSeconds: (expectedDurationSeconds: number) => {
      this.renderOptions.expectedDurationSeconds = expectedDurationSeconds;
    },
    setIsInProgress: (isInProgress: boolean) => {
      this.renderOptions.isInProgress = isInProgress;
    },
    setWidth: (widthPx: number) => {
      this.renderOptions.widthPx = widthPx;
      this.refresh();
    },
  };

  constructor(private initialOptions: InitialOptions) {
    this.currentMeasurement = initialOptions.measurement;
    this.renderOptions.isInProgress = initialOptions.isInProgress;
    this.renderOptions.expectedDurationSeconds = initialOptions.expectedDurationSeconds;
    this.renderOptions.widthPx = initialOptions.widthPx;
    this.initPlotly(initialOptions);
  }

  private async initPlotly({ container, measurement }: InitialOptions) {
    const { data, layout } = this.getDataForRender(measurement);
    const hasMoreThan1Coord = data.some((d) => d.y.length > 1);
    this.initialOptions.container.hidden = !hasMoreThan1Coord;
    this.chart = await Plotly.newPlot(container, data, layout, this.config);

    this.mediaQueryIsMobileSize.onchange = () => {
      this.refresh();
    };
  }

  public refresh(measurement = this.currentMeasurement) {
    const { data, layout } = this.getDataForRender(measurement);
    this.currentMeasurement = measurement;

    const hasMoreThan1Coord = data.some((d) => d.y.length > 1);
    this.initialOptions.container.hidden = !hasMoreThan1Coord;

    Plotly.react(this.initialOptions.container, data, layout, undefined, undefined);
  }

  private getDataForRender(measurement: Measurement): { data: DataExtended[]; layout: Layout } {
    const { zoomBounds, expectedDurationSeconds, widthPx } = this.renderOptions;

    const seconds = measurement.data.length / measurement.mps;
    const minutes = seconds / SECONDS_IN_MINUTE;
    const dx = minutes / measurement.data.length;

    const startTimeMinutes = zoomBounds?.x0 ?? null;
    const endTimeMinutes = zoomBounds?.x1 ?? null;
    const hasRangeBounds = startTimeMinutes != null || endTimeMinutes != null;

    const startIndex = Math.max(
      Math.floor(
        startTimeMinutes != null && startTimeMinutes >= 0
          ? startTimeMinutes * SECONDS_IN_MINUTE * measurement.mps
          : 0,
      ),
      0,
    );

    const yList = measurement.data;

    const endIndex = Math.min(
      endTimeMinutes != null && endTimeMinutes >= 0
        ? Math.ceil(endTimeMinutes * SECONDS_IN_MINUTE * measurement.mps)
        : yList.length,
      yList.length,
    );

    const padding = 1;
    const startIndexPadded = Math.max(startIndex - padding, 0);
    const endIndexPadded = Math.min(endIndex + padding, yList.length);

    const y = yList.slice(startIndexPadded, endIndexPadded);

    const x0 = hasRangeBounds ? Math.max(startIndexPadded * dx, 0) : 0;

    const data = [
      {
        y,
        dx,
        x0,
        mps: measurement.mps,
        hovertemplate: DATA_HOVER_TEMPLATE_FOR_PRESSURE,
        customdata: getHoverMessages(y, dx, x0, 'psi'),
        mode: 'lines' as const,
      },
    ];

    const pressureValues = yList.filter((value): value is number => Number.isFinite(value));
    const minPressure = Math.min(0, ...pressureValues);
    const maxPressure = Math.max(0, ...pressureValues);
    const pressureRange = maxPressure - minPressure;
    const pressurePadding = pressureRange > 0 ? pressureRange * 0.03 : 1;

    const tickValues = [0];
    const tickText = [`0`];

    if (minPressure < 0) {
      tickValues.unshift(minPressure);
      tickText.unshift(` ${numberFormatterWith2FractionDigits.format(minPressure)} psi`);
    }

    if (maxPressure > 0) {
      tickValues.push(maxPressure);
      tickText.push(` ${numberFormatterWith2FractionDigits.format(maxPressure)} psi`);
    }

    const layout: Layout = {
      showlegend: false,
      xaxis: {
        visible: true,
        autorange: false,
        ticksuffix: ' min',
        showticksuffix: 'last',
        showticklabels: false,
        showgrid: false,
        range: [zoomBounds?.x0 ?? 0, zoomBounds?.x1 ?? expectedDurationSeconds / SECONDS_IN_MINUTE],
        // To save current zoom
        uirevision: 'time',
        zerolinecolor: COLORS.zeroline,
        hoverformat: AXIS_HOVER_FORMAT,
        fixedrange: true,
        rangemode: 'normal',
      },
      yaxis: {
        // To save current zoom
        uirevision: 'time',
        // @ts-ignore
        ticklabeloverflow: 'allow',
        ticklabelposition: 'inside top',
        ticks: 'outside',
        tickcolor: COLORS.axisTick,
        tickfont: {
          color: COLORS.axisLabel,
        },
        zeroline: false,
        fixedrange: true,
        hoverformat: AXIS_HOVER_FORMAT,
        range: [minPressure - pressurePadding, maxPressure + pressurePadding],
        tickmode: 'array',
        tickvals: tickValues,
        // TODO Little hack to avoid rounding of tickvals. tickformat doesn't work. Check the source code to see why
        ticktext: tickText,
        rangemode: 'tozero',
        showline: true,
      },
      dragmode: false,
      margin: this.mediaQueryIsMobileSize.matches ? SIZES.margins.mobile : SIZES.margins.desktop,
      width: widthPx,
      height: 100,
      colorway: [COLORS.measurements.other],
    };

    return {
      data,
      layout,
    };
  }
}
